import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, InputAdornment, Paper, Stack, TextField, Typography } from '@mui/material';
import CheckCircle from '@mui/icons-material/CheckCircle';
import ClearAll from '@mui/icons-material/ClearAll';
import CheckBoxOutlineBlank from '@mui/icons-material/CheckBoxOutlineBlank';
import DirectionsRun from '@mui/icons-material/DirectionsRun';
import FitnessCenter from '@mui/icons-material/FitnessCenter';
import FavoriteBorder from '@mui/icons-material/FavoriteBorder';
import BedtimeOutlined from '@mui/icons-material/BedtimeOutlined';
import Straighten from '@mui/icons-material/Straighten';
import WaterDropOutlined from '@mui/icons-material/WaterDropOutlined';
import BloodtypeOutlined from '@mui/icons-material/BloodtypeOutlined';
import Search from '@mui/icons-material/Search';
import GridView from '@mui/icons-material/GridView';
import type { DeviceMetric } from '../../../../services/deviceMetricTypes';
import type { DeviceMetricModule, MetricProviderId, MetricSelectionData } from './types';
import { metricPresentation } from './metricPresentation';
import { ExportSectionHeading, exportSectionSx } from '../ExportFormSection';
import MetricVerification from './MetricVerification';
import DeviceMeasurementGuide from './DeviceMeasurementGuide';
import DeviceMetricCard from './DeviceMetricCard';
import { getDeviceMetricModule, metricModule } from './registry';
import { useDeviceMetricProvider } from './DeviceMetricProvider';

const icons: Record<string, typeof GridView> = {
  activity: DirectionsRun,
  workouts: FitnessCenter,
  heart: FavoriteBorder,
  sleep: BedtimeOutlined,
  body: Straighten,
  nutrition: WaterDropOutlined,
  glucose: BloodtypeOutlined,
  other: GridView,
};

type CategoryFilter = string;

export interface DeviceMetricPickerProps {
  provider?: MetricProviderId;
  summary: MetricSelectionData | null;
  catalog?: DeviceMetric[];
  value: string | null;
  onChange: (route: string) => void;
  onSelectionChange?: (routes: string[]) => void;
  onClear: () => void;
  allowAll?: boolean;
  selectedValues?: string[];
  collapseKey?: number;
  selectionOnly?: boolean;
  showStepNumber?: boolean;
}

export default function DeviceMetricPicker({ provider, ...props }: DeviceMetricPickerProps) {
  const suppliedMetrics = props.summary?.catalog || props.catalog || [];
  const inheritedProvider = useDeviceMetricProvider();
  const deviceModule = getDeviceMetricModule(provider || inheritedProvider || (suppliedMetrics[0] ? metricModule(suppliedMetrics[0]).id : 'fitbit'));
  // Keying on the device gives each one its own search, category and expansion state.
  return <MetricPicker key={deviceModule.id} {...props} deviceModule={deviceModule} />;
}

function MetricPicker({
  deviceModule,
  summary,
  catalog: suppliedCatalog,
  value,
  onChange,
  onSelectionChange,
  onClear,
  allowAll = false,
  selectedValues,
  collapseKey,
  selectionOnly = false,
  showStepNumber = true,
}: Omit<DeviceMetricPickerProps, 'provider'> & { deviceModule: DeviceMetricModule }) {
  // Another device's catalog can linger while a source change loads.
  const catalog = (summary?.catalog || suppliedCatalog || []).filter(metric => metricModule(metric).id === deviceModule.id);
  const showAvailability = !!summary && !selectionOnly;
  const multiple = selectedValues !== undefined;
  const allSelected = multiple
    ? catalog.length > 0 && catalog.every(metric => selectedValues.includes(metric.routeKey))
    : allowAll && value === '';
  const hasSelection = multiple ? selectedValues.length > 0 : allSelected || !!value;
  const selected = catalog.find(metric => metric.routeKey === value);
  const selectedCategory = selected ? metricPresentation(selected).category : 'all';
  const [category, setCategory] = useState<CategoryFilter>(selectedCategory);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(true);
  const list = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (collapseKey) setExpanded(false);
  }, [collapseKey]);
  useEffect(() => {
    if (list.current) list.current.scrollTop = 0;
  }, [category, search]);

  // Selecting a measurement leaves the current category, search, and list open.
  const choose = (route: string) => onChange(route);
  const chooseCategory = (next: CategoryFilter) => {
    setCategory(next);
    setSearch('');
    setExpanded(true);
  };
  const counts = new Map(summary?.metrics.map(metric => [metric.routeKey, Number(metric.presentRecords)]) || []);
  const days = new Map<string, Set<string>>();
  for (const row of summary?.dailyCoverage || []) {
    if (Number(row.presentRecords) > 0) {
      const dates = days.get(row.routeKey) || new Set<string>();
      dates.add(row.date);
      days.set(row.routeKey, dates);
    }
  }
  const withData = catalog.filter(metric => (counts.get(metric.routeKey) || 0) > 0).length;
  const searchText = search.trim().toLowerCase();
  const visible = catalog.filter(metric => {
    const item = metricPresentation(metric);
    const matchesReview = category !== 'verified' || item.verified;
    const matchesCategory = searchText || category === 'all' || category === 'verified' || item.category === category;
    const matchesSearch = `${item.label} ${item.description} ${metric.dataType}`.toLowerCase().includes(searchText);
    return matchesReview && matchesCategory && matchesSearch;
  }).sort((a, b) =>
    Number((counts.get(b.routeKey) || 0) > 0) - Number((counts.get(a.routeKey) || 0) > 0) ||
    metricPresentation(a).label.localeCompare(metricPresentation(b).label)
  );
  const filtered = category !== 'all' || !!searchText;
  const visibleSelected = visible.length > 0 && visible.every(metric => selectedValues?.includes(metric.routeKey));
  const selectAll = () => {
    if (allSelected) { onClear(); return; }
    if (allowAll) choose('');
    else onSelectionChange?.(catalog.map(metric => metric.routeKey));
  };
  const selectVisible = () => {
    const visibleRoutes = new Set(visible.map(metric => metric.routeKey));
    onSelectionChange?.(visibleSelected
      ? (selectedValues || []).filter(route => !visibleRoutes.has(route))
      : Array.from(new Set([...(selectedValues || []), ...Array.from(visibleRoutes)])));
  };
  const selectionAction = filtered && visibleSelected ? 'Deselect' : 'Select';
  const categorySelectionLabel = searchText
    ? `${selectionAction} search results`
    : category === 'verified' ? `${selectionAction} verified metrics` : `${selectionAction} all in this category`;

  return <Paper variant="outlined" component="section" aria-label={`${deviceModule.label} measurements`} sx={selectionOnly ? exportSectionSx : { p: { xs: 2, md: 2.5 }, borderRadius: 3, mb: 3 }}>
    {selectionOnly && (showStepNumber ? <ExportSectionHeading step={3} title="Measurements" action={<DeviceMeasurementGuide provider={deviceModule.id} compact />} /> :
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}><Typography component="h2" variant="subtitle1" fontWeight={650}>Measurements</Typography><DeviceMeasurementGuide provider={deviceModule.id} compact /></Stack>)}
    {(!selectionOnly || expanded) && <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: selectionOnly ? 1.25 : 2 }}>
      {!selectionOnly && <Box><Typography variant="h6" component="h2">{multiple ? 'Select measurements' : 'Explore measurements'}</Typography>
        <Typography variant="body2" color="text.secondary">{showAvailability ? `${withData} of ${catalog.length} measurements have data in these dates.` : 'Choose measurements, then click Visualize to load their data.'}</Typography></Box>}
      {expanded && <TextField size="small" label="Find a measurement" placeholder="Try steps, sleep or heart rate" value={search}
        onChange={event => setSearch(event.target.value)} fullWidth={selectionOnly} sx={{ minWidth: selectionOnly ? 0 : { sm: 280 } }}
        InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />}
    </Stack>}
    {expanded && !selectionOnly && <Box sx={{ mb: 1.5 }}><DeviceMeasurementGuide provider={deviceModule.id} /></Box>}
    <Stack direction="row" gap={selectionOnly ? .75 : 1} flexWrap="wrap" aria-label="Measurement categories" sx={{ mb: selectionOnly ? 1.25 : 2,
      ...(selectionOnly && { '& .MuiButton-root': { minHeight: 32, py: .5, px: 1.25, fontSize: 12 } }) }}>
      <Button size="small" variant="outlined" aria-pressed={category === 'all' && !search}
        onClick={() => chooseCategory('all')} sx={{ borderRadius: 6, color: '#526171',
          bgcolor: category === 'all' && !search ? '#e7ecf2' : '#f6f7f9', borderColor: '#bdc8d4',
          boxShadow: category === 'all' && !search ? 'inset 0 0 0 1px #627083' : 'none',
          '&:hover': { bgcolor: '#e7ecf2', borderColor: '#627083' } }}>{selectionOnly ? 'All categories' : 'All measurements'}</Button>
      <Button size="small" variant="outlined" startIcon={<CheckCircle />} aria-pressed={category === 'verified'}
        onClick={() => chooseCategory(category === 'verified' ? 'all' : 'verified')}
        sx={{ borderRadius: 6, color: '#366b53', bgcolor: category === 'verified' ? '#e0ede5' : '#f0f6f2',
          borderColor: category === 'verified' ? '#366b53' : '#c9dbcf', fontWeight: category === 'verified' ? 700 : 500,
          boxShadow: category === 'verified' ? 'inset 0 0 0 1px #366b53' : 'none',
          '&:hover': { bgcolor: '#e0ede5', borderColor: '#366b53' },
          '&.Mui-focusVisible': { outline: '2px solid #366b53', outlineOffset: 2 } }}>Verified metric</Button>
      {Object.keys(deviceModule.categories).filter(key => catalog.some(metric => metricPresentation(metric).category === key)).map(key => {
        const Icon = icons[key] || GridView;
        const palette = deviceModule.categories[key];
        const selectedCategory = category === key && !search;
        return <Button key={key} size="small" startIcon={<Icon />} aria-pressed={selectedCategory}
          variant="outlined" onClick={() => chooseCategory(key)}
          sx={{ borderRadius: 6, color: palette.color, bgcolor: selectedCategory ? palette.selectedBackground : palette.background,
            borderColor: selectedCategory ? palette.color : palette.border, fontWeight: selectedCategory ? 700 : 500,
            boxShadow: selectedCategory ? `inset 0 0 0 1px ${palette.color}` : 'none',
            '&:hover': { bgcolor: palette.selectedBackground, borderColor: palette.color },
            '&.Mui-focusVisible': { outline: `2px solid ${palette.color}`, outlineOffset: 2 } }}>{palette.label}</Button>;
      })}
    </Stack>
    {expanded && (allowAll || (multiple && onSelectionChange)) && <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} sx={{ mb: selectionOnly ? 1.25 : 2 }}>
      <Button size="small" onClick={selectAll} aria-pressed={allSelected} variant="outlined" disabled={!catalog.length}
        startIcon={allSelected ? <CheckCircle fontSize="small" /> : <CheckBoxOutlineBlank fontSize="small" />}
        sx={{ flex: 1, borderRadius: '10px', minHeight: 36, bgcolor: allSelected ? '#fcf5f5' : 'transparent', borderColor: allSelected ? 'primary.main' : 'divider' }}>
        {allSelected ? 'Deselect all measurements' : 'Select all measurements'}
      </Button>
      {multiple && onSelectionChange && <Button size="small" onClick={selectVisible} variant="outlined"
        disabled={!filtered || !visible.length} aria-pressed={filtered && visibleSelected}
        startIcon={filtered && visibleSelected ? <CheckCircle fontSize="small" /> : <CheckBoxOutlineBlank fontSize="small" />}
        sx={{ flex: 1, borderRadius: '10px', minHeight: 36, bgcolor: filtered && visibleSelected ? '#fcf5f5' : 'transparent',
          borderColor: filtered && visibleSelected ? 'primary.main' : 'divider' }}>
        {categorySelectionLabel}
      </Button>}
    </Stack>}
    {expanded && <Box ref={list} role="group" aria-label="Measurement choices" sx={{ display: 'grid',
      gridTemplateColumns: selectionOnly ? 'repeat(auto-fill, minmax(min(100%, 158px), 1fr))' : { xs: '1fr', sm: `repeat(${Math.min(2, visible.length || 1)}, minmax(0, 1fr))`, lg: `repeat(${Math.min(4, visible.length || 1)}, minmax(0, 1fr))` }, gap: selectionOnly ? .75 : 1.25,
      maxHeight: selectionOnly ? { xs: 420, md: 560, lg: 640 } : 320, overflowY: 'auto', p: .5, mx: -.5, scrollbarWidth: 'thin' }}>
      {visible.map(metric => (
        <DeviceMetricCard
          key={metric.routeKey}
          metric={metric}
          active={multiple ? selectedValues.includes(metric.routeKey) : value === metric.routeKey}
          multiple={multiple}
          selectionOnly={selectionOnly}
          showAvailability={showAvailability}
          count={counts.get(metric.routeKey) || 0}
          recordedDays={days.get(metric.routeKey)?.size || 0}
          onChoose={choose}
        />
      ))}
    </Box>}
    {expanded && !visible.length && <Typography color="text.secondary" sx={{ py: 2 }}>No matching measurements. Try another name or category.</Typography>}
    <Stack direction={{ xs: 'column', sm: 'row' }} flexWrap="wrap" justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={1} sx={{ mt: selectionOnly ? 1.25 : 1.5 }}>
      <Box>
        <Typography variant="body2" color="text.secondary">{multiple ? `${selectedValues.length} selected${selectionOnly ? ' · Choose one or more' : ' · Select cards to add or remove measurements'}` : expanded && !selectionOnly ? `${visible.length} measurements · Scroll to browse` : `Selected: ${selected ? metricPresentation(selected).label : allSelected ? 'All measurements' : 'None'}`}</Typography>
        {!expanded && !multiple && selected && metricPresentation(selected).unverified &&
          <Box sx={{ mt: 1 }}><MetricVerification metric={selected} /></Box>}
      </Box>
      <Stack direction="row" flexWrap="wrap" gap={1} sx={{ flexShrink: 0 }}>
        <Button size="small" startIcon={<ClearAll />} onClick={onClear} disabled={!hasSelection}>Clear selection</Button>
        <Button size="small" aria-expanded={expanded} onClick={() => setExpanded(!expanded)} sx={{ ...(selectionOnly && { px: 1, minHeight: 36 }) }}>{expanded ? 'Hide measurements' : multiple ? 'Change measurements' : 'Change measurement'}</Button>
      </Stack>
    </Stack>
  </Paper>;
}
